import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import PurchaseOrderDetailScreen from "./PurchaseOrderDetailScreen.jsx";
import {
  PO_STATUS_OPTIONS,
  filterPurchaseOrders,
  findPurchaseOrderByNumber,
  groupRfqBundles,
  loadPurchaseOrders,
  poLateInfo,
  rfqBundleName,
} from "../../features/purchaseOrders/poData";
import { shopToday } from "../../utils/shopDate";
import { fmtDay, fmtLocalDay } from "./salesOrders/format";
import "../mobile.css";

// The phone Purchase Orders page (plan Phase 2, READ-ONLY): the list with the desktop's status
// filter and search (PO #, supplier, project; vendor SO on the API), RFQ bundles as one card; ?id=N
// opens one PO (PurchaseOrderDetailScreen), ?po=<PO #> (the desktop's link) finds it first. Nothing
// here writes: creating, editing, receiving, RFQs and awards, emails and labels stay on the desktop.
// Layout only: features/purchaseOrders/poData.js is the desktop page's own code.

function poId(searchParams) {
  const raw = searchParams.get("id");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

const LIST_START = { statusFilter: "Committed", searchTerm: "", vendorSo: "" };

export default function PurchaseOrdersScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listState, setListState] = useState(LIST_START);
  const navigate = useNavigate();
  const fromList = useRef(false);
  const id = poId(searchParams);
  const poNumber = searchParams.get("po");
  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else setSearchParams({}, { replace: true });
  };
  if (id) return <PurchaseOrderDetailScreen key={id} purchaseOrderId={id} onAll={toList} />;
  if (poNumber) return <FindByNumber poNumber={poNumber} onFound={(pid) => setSearchParams({ id: String(pid) }, { replace: true })} onAll={toList} />;
  return <PoList state={listState} setState={setListState} onOpen={(pid) => { fromList.current = true; setSearchParams({ id: String(pid) }); }} />;
}

/** ?po=1380 (the desktop's link, e.g. from the Action Center): the PO with that number. */
function FindByNumber({ poNumber, onFound, onAll }) {
  const [missing, setMissing] = useState(null);
  useEffect(() => {
    let live = true;
    findPurchaseOrderByNumber(poNumber)
      .then((hit) => {
        if (!live) return;
        if (hit) onFound(hit.purchase_order_id); else setMissing(`PO ${poNumber} isn't in JobQ.`);
      })
      .catch((err) => { if (live) setMissing(err.message || "Couldn't reach JobQ."); });
    return () => { live = false; };
  }, [poNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="jq-screen">
      {missing
        ? <><div className="jq-note jq-note-danger mb-2" role="alert">{missing}</div><button type="button" className="btn btn-outline-secondary" onClick={onAll}>All purchase orders</button></>
        : <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Finding PO {poNumber}…</div>}
    </div>
  );
}

function PoCard({ po, today }) {
  const { isLate, daysLate } = poLateInfo(po, today);
  return (
    <div data-testid={`po-card-${po.purchase_order_id}`} className={po.inBundle ? "ps-3 border-start" : undefined}>
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">PO {po.po_number}</span>
        <span className="jq-tag">{po.status}</span>
        {isLate && <span className="jq-tag jq-tag-block">LATE {daysLate}d</span>}
        {po.email_count > 0 && <span className="jq-tag" title={`Emailed ${po.email_count} time(s)`}>✉ {po.email_count}</span>}
      </div>
      <div className="jq-qcard-title">{po.supplier || "No supplier"}</div>
      <div className="jq-qcard-meta">
        {po.estimated_receive_date ? `Due ${fmtDay(po.estimated_receive_date)}` : "No date"}
        {po.confirmed ? " · confirmed by supplier" : ""}
        {po.vendor_sales_order ? ` · vendor SO ${po.vendor_sales_order}` : ""}
      </div>
      {po.project_name && <div className="jq-muted">Project: {po.project_name}</div>}
    </div>
  );
}

/** An RFQ sent to several vendors: one card; a tap lists its vendors' POs under it. */
function BundleCard({ bundle, open }) {
  const name = rfqBundleName(bundle);
  return (
    <div data-testid={`po-bundle-${bundle.rfq_group_id}`}>
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">{name || "RFQ bundle"}</span>
        <span className="jq-tag jq-tag-warn">Bidding</span>
      </div>
      <div className="jq-qcard-meta">
        {bundle.childPOs.length} vendors invited · sent {fmtLocalDay(bundle.created_at)} · {open ? "tap to hide" : "tap for their POs"}
      </div>
    </div>
  );
}

function PoList({ state, setState, onOpen }) {
  const { statusFilter, searchTerm, vendorSo } = state;
  const set = (key) => (value) => setState((prev) => ({ ...prev, [key]: value }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openBundle, setOpenBundle] = useState(null);
  const today = shopToday();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    loadPurchaseOrders({ status: statusFilter, vendorSalesOrder: vendorSo.trim(), signal: controller.signal })
      .then((data) => { setRows(Array.isArray(data) ? data : []); setError(null); setLoading(false); })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setRows([]); // never the last filter's POs under the error
        setError(err.message || "Couldn't reach JobQ.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [statusFilter, vendorSo]);

  const shown = useMemo(
    () => groupRfqBundles(filterPurchaseOrders(rows, { searchTerm, statusFilter })),
    [rows, searchTerm, statusFilter],
  );
  // An open bundle's vendor POs follow it as rows of their own.
  const items = useMemo(
    () => shown.flatMap((p) => (p.isGroupHeader && p.rfq_group_id === openBundle
      ? [p, ...p.childPOs.map((c) => ({ ...c, inBundle: true }))]
      : [p])),
    [shown, openBundle],
  );
  const activeCount = (statusFilter !== "Committed" ? 1 : 0) + (vendorSo ? 1 : 0);

  return (
    <div className="jq-screen" data-testid="phone-purchase-orders">
      <CardList
        items={items}
        getKey={(p) => (p.isGroupHeader ? `rfq-${p.rfq_group_id}` : p.purchase_order_id)}
        onItemClick={(p) => {
          if (p.isGroupHeader) setOpenBundle(openBundle === p.rfq_group_id ? null : p.rfq_group_id);
          else onOpen(p.purchase_order_id);
        }}
        renderCard={(p) => (p.isGroupHeader
          ? <BundleCard bundle={p} open={openBundle === p.rfq_group_id} />
          : <PoCard po={p} today={today} />)}
        loading={loading}
        error={error}
        empty={rows.length ? "No purchase orders match." : "No purchase orders."}
        testId="po-list"
        search={{ value: searchTerm, onChange: set("searchTerm"), placeholder: "PO #, supplier or project" }}
        filters={{
          activeCount,
          resultLabel: `${shown.length} row${shown.length === 1 ? "" : "s"}`,
          onClear: () => setState((prev) => ({ ...prev, statusFilter: "Committed", vendorSo: "" })),
          render: () => (
            <>
              <div className="form-label">Status</div>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {PO_STATUS_OPTIONS.map((o) => (
                  <button key={o.value || "all"} type="button" className={`btn btn-sm ${statusFilter === o.value ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={statusFilter === o.value} onClick={() => set("statusFilter")(o.value)}>
                    {o.label}
                  </button>
                ))}
              </div>
              <label className="form-label" htmlFor="po-vendor-so">Vendor SO</label>
              <input id="po-vendor-so" className="form-control" value={vendorSo} onChange={(e) => set("vendorSo")(e.target.value)} placeholder="The vendor's order number" />
            </>
          ),
        }}
      />
    </div>
  );
}
